// src/components/ThreatFeedApiCta.jsx
//
// Inline sales chip on the Threat Feeds page nudging users toward the API
// add-on. Unlike the bottom-right UpgradeCtaToast on Global Search this is
// a *passive* chip — it sits near the page header and stays out of the way
// of the actual feed. The backend decides visibility via `shows_cta` so we
// never accidentally hawk to users who already own the add-on.

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { WandSparkles, X } from "lucide-react";

const STATUS_URL =
  "https://whiteintel.io/api/regular/app/v2/user/threatfeed_api_status.php";

const DISMISS_KEY = "wi.threatFeedApiCtaDismissed";

function getJwtToken() {
  try {
    return (
      localStorage.getItem("jwtToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      ""
    );
  } catch {
    return "";
  }
}

export default function ThreatFeedApiCta() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(STATUS_URL, {
          headers: { Authorization: `Bearer ${getJwtToken()}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setStatus(data);
      } catch {
        // Silent — non-critical CTA, never blow up the page.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dismissed]);

  // Trust the backend `shows_cta` flag. Falls back to "has_api_access === false"
  // so the chip still works once the endpoint is live but the flag isn't sent.
  const shouldShow =
    !dismissed &&
    status &&
    (status.shows_cta === true || status.has_api_access === false);

  if (!shouldShow) return null;

  const onDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {}
    setDismissed(true);
  };

  return (
    <div className="inline-flex items-center gap-2 rounded-md border border-sky-500/30 bg-sky-500/10 pl-2.5 pr-1.5 py-1 text-[12px] text-sky-200">
      <WandSparkles className="h-3.5 w-3.5 text-sky-300 shrink-0" />
      <span className="hidden sm:inline">Automate access to this feed via API.</span>
      <span className="sm:hidden">Automate via API?</span>
      <button
        type="button"
        onClick={() => navigate("/upgrades?tab=addons")}
        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold text-sky-100 hover:text-white hover:bg-sky-500/20 transition-colors"
      >
        View plans →
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="rounded p-0.5 text-sky-400 hover:text-white hover:bg-white/[0.06] transition-colors"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
